import { KvmConfiguration } from "@domain/project/configurations/monitoring/kvm-configuration";
import { Floorplan } from "@domain/project/floorplan/floorplan";
import { FloorplanEventType } from "@domain/project/floorplan/floorplan-event";
import { FloorplanItem } from "@domain/project/floorplan/floorplan-item";
import { FloorplanProductItem } from "@domain/project/floorplan/floorplan-product-item";
import { FloorplanKvmReceiver } from "@domain/project/floorplan/monitoring/floorplan-kvm-receiver";
import { IsArrayOfInstancesOf } from "@utils/class-validator/class-validator-constraints";
import { Exclude, Expose, Transform, Type } from "class-transformer";
import { IsString, ValidateNested } from "class-validator";
import { v4 as uuidv4 } from "uuid";

export class FloorplanKvm extends FloorplanProductItem<KvmConfiguration> {
  @Exclude()
  private readonly _kvm: KvmConfiguration;

  @IsString()
  @Expose({ name: "kvmId" })
  private readonly _kvmId: string;

  @IsArrayOfInstancesOf(FloorplanKvmReceiver)
  @ValidateNested({ each: true })
  @Type(() => FloorplanKvmReceiver)
  @Transform((params) => params.value || [], { toClassOnly: true })
  @Expose({ name: "receivers" })
  private _receivers: FloorplanKvmReceiver[] = [];

  constructor(floorplan: Floorplan, kvm: KvmConfiguration, id: string, x: number, y: number, size: number, notes?: string) {
    super(floorplan, kvm, id, x, y, size, notes);
    this._kvmId = kvm?.id;
    this._kvm = kvm;
  }

  override init(floorplan: Floorplan) {
    super.init(floorplan);
    if (!this.config) {
      return;
    }
    this._receivers.forEach((receiver) => receiver.init(floorplan, () => this.locked));
  }

  protected override getConfigFromProject(floorplan: Floorplan): KvmConfiguration | undefined {
    return floorplan.getKvmConfig(this._kvmId);
  }

  setItemTextVisibility() {
    this.itemTextVisible = this._floorplan.floorplanState.kvmsItemTextVisible;
  }

  get receivers(): FloorplanKvmReceiver[] {
    return this._receivers;
  }

  reconcileReceiversWithConfig(): boolean {
    const receiverQuantity = this.config?.receiver?.quantity ?? 0;
    const currentReceivers = this._receivers;
    const nextReceivers = currentReceivers.slice(0, receiverQuantity);

    while (nextReceivers.length < receiverQuantity) {
      nextReceivers.push(this.createReceiver(nextReceivers.length));
    }

    if (nextReceivers.length === currentReceivers.length) {
      return false;
    }

    this._receivers = nextReceivers;
    return true;
  }

  protected override createRawDuplicate(): ((x: number, y: number) => FloorplanKvm) | undefined {
    const config = this.config;
    const size = this.size;

    return (x: number, y: number) => {
      const dup = FloorplanKvm.create(this._floorplan, config, x, y, size);
      dup.reconcileReceiversWithConfig();
      return dup;
    };
  }

  override getDuplicateTargetCollection() {
    return this._floorplan.kvms;
  }

  protected override registerDuplicate() {
    return (duplicate: FloorplanItem, afterDuplicateAdded?: (duplicate: FloorplanItem) => void) =>
      this.registerDuplicateInFloorplan(
        duplicate as FloorplanKvm,
        this._floorplan.kvms,
        FloorplanEventType.KVM_ADDED,
        () => this._floorplan.project.setPositionNumbers(),
        (dup: FloorplanKvm) => {
          afterDuplicateAdded?.(dup);
        },
      );
  }

  override delete() {
    this._floorplan.deleteKvm(this);
  }

  get relatedItems(): FloorplanItem[] {
    return this._floorplan.kvms;
  }

  protected override refreshFloorplanLockState() {
    this._floorplan.floorplanState.refreshKvmsLock();
  }

  private createReceiver(index: number): FloorplanKvmReceiver {
    return FloorplanKvmReceiver.create(this._floorplan, () => this.locked, this.x + (index + 1) * 100, this.y + 100, this.size);
  }

  static create(floorplan: Floorplan, kvm: KvmConfiguration, x: number, y: number, size: number): FloorplanKvm {
    return new FloorplanKvm(floorplan, kvm, uuidv4(), x, y, size);
  }
}
